import { useColorScheme } from '@mui/material/styles';
import type {
  AgTooltipRendererResult,
  AgTopologyChartOptions
} from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import { useMemo } from 'react';

import { getAgChartsTheme } from '@/components/charts/charts.utils';
import type { AssetConnectionMetricsDTO } from '@/pages/Assets';
import { getCountryName } from '@/utils/Common.utils';

import { WORLD_MAP_CHART_TOPOLOGY } from './WorldMapChart.constants';

const WorldMapChart = ({ data }: { data: AssetConnectionMetricsDTO[] }) => {
  const { mode: themeMode } = useColorScheme();

  const mapData = useMemo(() => {
    const countryMap = new Map<string, number>();

    // Aggregate count by country
    data?.forEach((item) => {
      const countryName = getCountryName(item.country_code as string);
      if (countryName) {
        const currentCount = countryMap.get(countryName) || 0;
        countryMap.set(countryName, currentCount + item.count);
      }
    });

    // Convert to array format for AG Charts
    return Array.from(countryMap.entries()).map(([name, count]) => ({
      name,
      value: count,
      assetCount: count
    }));
  }, [data]);

  const options: AgTopologyChartOptions = {
    height: 500,
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0
    },
    topology: WORLD_MAP_CHART_TOPOLOGY,
    series: [
      {
        type: 'map-shape-background',
        topology: WORLD_MAP_CHART_TOPOLOGY
      },
      {
        type: 'map-marker',
        topology: WORLD_MAP_CHART_TOPOLOGY,
        data: mapData,
        title: 'Connections Per Country',
        idKey: 'name',
        idName: 'Country',
        sizeKey: 'assetCount',
        sizeName: 'Count',
        topologyIdKey: 'NAME_ENGL',
        size: 5,
        labelKey: 'name',
        tooltip: {
          renderer: ({ datum }) =>
            ({
              content: `${datum.name}: ${datum.assetCount} assets`
            }) as string | AgTooltipRendererResult
        }
      }
    ],
    theme: getAgChartsTheme(themeMode),
    overlays: {
      noData: {
        enabled: false
      }
    }
  };

  return <AgCharts options={options} />;
};

export default WorldMapChart;
